import {Timestamp} from '@wandb/weave/components/Timestamp';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {CellInfo} from 'react-table';

import {job as jobUrl} from '../../../util/urls';
import {Table} from '../Table';
import {TruncatedMarkdown} from '../TruncatedMarkdown';
import {DeleteJobDialog} from './DeleteJobDialog';
import * as S from './JobsTable.styles';
import {JobsTableActions} from './JobsTableActions';
import {JobInfo} from './jobUtil';

interface JobsTableProps {
  entityName: string;
  projectName: string;

  jobs: JobInfo[];
  onRun: (jobId: string) => void;
  onJobDelete: () => void;
}

export const JobsTable: React.FC<JobsTableProps> = ({
  entityName,
  projectName,
  jobs,
  onRun,
  onJobDelete,
}) => {
  // TODO: Is there a CSS way to handle this?
  const [hoveredRow, setHoveredRow] = useState(null);

  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [deletingJobName, setDeletingJobName] = useState<string | null>(null);
  const onDelete = (jobId: string, jobName: string) => {
    setDeletingJobId(jobId);
    setDeletingJobName(jobName);
  };
  const closeDialog = () => {
    setDeletingJobId(null);
  };

  const rows = jobs.map(job => {
    const {id, name, description, createdAt, artifacts, versionInfo} = job;
    const urlParams = {
      entityName,
      projectName,
      jobId: id,
      jobVersion: versionInfo.runTarget,
      tab: artifacts.length ? 'runs' : 'job_details',
    };
    const url = jobUrl(urlParams);
    return {
      searchString: name + '|' + description,
      row: {
        id,
        name,
        description,
        url,
        created: createdAt,
        versions: artifacts.length,
        ...versionInfo,
      },
    };
  });

  const columns = [
    {
      Header: 'Job',
      accessor: 'name',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <>
            <S.Name>
              <Link to={cellInfo.original.url}>{cellInfo.value}</Link>
            </S.Name>
            <S.Description>
              <TruncatedMarkdown
                content={cellInfo.original.description}
                maxLength={140}
                firstLineOnly={true}
                tooltipPosition="bottom left"
              />
            </S.Description>
          </>
        );
      },
    },
    {
      Header: 'Versions',
      accessor: 'versions',
      width: 100,
    },
    {
      Header: 'Runs',
      accessor: 'runsCount',
      width: 100,
    },
    {
      Header: 'Creation date',
      accessor: 'created',
      Cell: (cellInfo: CellInfo) => {
        return <Timestamp value={cellInfo.value} />;
      },
    },
    {
      Header: 'Last run',
      accessor: 'mostRecentRun',
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value != null ? (
          <Timestamp value={cellInfo.value} />
        ) : (
          'N/A'
        );
      },
    },
    {
      accessor: 'id',
      width: 200,
      sortable: false,
      Cell: (cellInfo: CellInfo) => {
        if (hoveredRow !== cellInfo.value) {
          return null;
        }
        return (
          <JobsTableActions
            entityName={entityName}
            projectName={projectName}
            jobId={cellInfo.value}
            jobName={cellInfo.original.name}
            jobVersion={cellInfo.original.runTarget}
            versionCount={cellInfo.original.versions}
            onRun={onRun}
            onDelete={onDelete}
          />
        );
      },
    },
  ];

  return (
    <>
      <DeleteJobDialog
        jobId={deletingJobId}
        jobName={deletingJobName}
        onClose={closeDialog}
        onDelete={onJobDelete}
      />
      <Table
        data={rows}
        columns={columns}
        pageSize={20}
        getTrProps={(state: any, rowInfo: any) => {
          if (rowInfo && rowInfo.row) {
            return {
              onMouseEnter: (e: React.MouseEvent) => {
                setHoveredRow(rowInfo.original.id);
              },
              onMouseLeave: (e: React.MouseEvent) => {
                setHoveredRow(null);
              },
              style: {
                minHeight: '50px',
              },
            };
          } else {
            return {};
          }
        }}
      />
    </>
  );
};
